import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import {
  AlertTriangle, Settings, Users, Send, Clock, Power, PowerOff,
  Bell, Mail, MessageSquare, RefreshCw, Play, Pause, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const MaintenanceManagement = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    scheduled_at: '',
    duration_minutes: 60,
    message: 'System maintenance in progress. We apologize for any inconvenience.',
    eta_message: '',
    send_email: true,
    send_sms: false
  });
  
  // Warning form state
  const [warningForm, setWarningForm] = useState({
    message: 'System maintenance will begin shortly. Please save your work and log out.',
    countdown_minutes: 15
  });
  
  // Global message form state
  const [messageForm, setMessageForm] = useState({
    title: '',
    message: '',
    message_type: 'info',
    send_email: false,
    send_sms: false
  });
  
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statusRes = await api.get('/maintenance/status');
      setStatus(statusRes.data);
      
      if (activeTab === 'sessions') {
        const sessionsRes = await api.get('/superadmin/maintenance/active-sessions');
        setSessions(sessionsRes.data);
      }
      
      if (activeTab === 'messages') {
        const messagesRes = await api.get('/superadmin/global-messages');
        setMessages(messagesRes.data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleMaintenance = async () => {
    if (!scheduleForm.scheduled_at) {
      toast.error('Please select a date and time');
      return;
    }
    
    try {
      const response = await api.post('/superadmin/maintenance/schedule', scheduleForm);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule maintenance');
    }
  };

  const handleSendWarning = async () => {
    try {
      const response = await api.post('/superadmin/maintenance/warning', null, {
        params: {
          message: warningForm.message,
          countdown_minutes: warningForm.countdown_minutes
        }
      });
      toast.success('Warning banner sent to all users');
      fetchData();
    } catch (error) {
      toast.error('Failed to send warning');
    }
  };

  const handleActivate = async () => {
    try {
      const response = await api.post('/superadmin/maintenance/activate');
      toast.success(`Maintenance mode activated. ${response.data.sessions_cleared} users logged out.`);
      setShowActivateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to activate maintenance mode');
    }
  };

  const handleDeactivate = async () => {
    try {
      await api.post('/superadmin/maintenance/deactivate');
      toast.success('Maintenance mode deactivated');
      setShowDeactivateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate maintenance mode');
    }
  };

  const handleCancelScheduled = async () => {
    try {
      await api.post('/superadmin/maintenance/cancel');
      toast.success('Scheduled maintenance cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel maintenance');
    }
  };

  const handleSendGlobalMessage = async () => {
    if (!messageForm.title || !messageForm.message) {
      toast.error('Please fill in title and message');
      return;
    }
    
    try {
      const response = await api.post('/superadmin/global-message', messageForm);
      toast.success('Global message sent');
      setMessageForm({ title: '', message: '', message_type: 'info', send_email: false, send_sms: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await api.delete(`/superadmin/global-message/${messageId}`);
      toast.success('Message removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove message');
    }
  };

  const getStatusBadge = () => {
    if (status?.is_active) {
      return <Badge className="bg-red-500 text-white">MAINTENANCE ACTIVE</Badge>;
    }
    if (status?.is_scheduled) {
      return <Badge className="bg-amber-500 text-white">SCHEDULED</Badge>;
    }
    if (status?.warning_active) {
      return <Badge className="bg-orange-500 text-white">WARNING ACTIVE</Badge>;
    }
    return <Badge className="bg-green-500 text-white">NORMAL OPERATION</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="maintenance-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Mode</h1>
          <p className="text-gray-500">Manage system maintenance and global messaging</p>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge()}
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={status?.is_active ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${status?.is_active ? 'bg-red-100' : 'bg-gray-100'}`}>
                  {status?.is_active ? <PowerOff className="h-6 w-6 text-red-600" /> : <Power className="h-6 w-6 text-gray-600" />}
                </div>
                <div>
                  <h3 className="font-semibold">Maintenance Mode</h3>
                  <p className="text-sm text-gray-500">
                    {status?.is_active ? 'Currently Active' : 'Not Active'}
                  </p>
                </div>
              </div>
              {status?.is_active ? (
                <Button variant="outline" onClick={() => setShowDeactivateDialog(true)} data-testid="deactivate-btn">
                  Deactivate
                </Button>
              ) : (
                <Button variant="destructive" onClick={() => setShowActivateDialog(true)} data-testid="activate-btn">
                  Activate Now
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Active Sessions</h3>
                  <p className="text-2xl font-bold text-blue-600">{sessions?.total_active_sessions || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={status?.is_scheduled ? 'border-amber-300 bg-amber-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${status?.is_scheduled ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <Clock className={`h-6 w-6 ${status?.is_scheduled ? 'text-amber-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">Scheduled</h3>
                  <p className="text-sm text-gray-500">
                    {status?.is_scheduled ? new Date(status.scheduled_at).toLocaleString() : 'None'}
                  </p>
                </div>
              </div>
              {status?.is_scheduled && (
                <Button variant="outline" size="sm" onClick={handleCancelScheduled}>
                  Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="status">
            <Settings className="mr-2 h-4 w-4" />
            Schedule & Warning
          </TabsTrigger>
          <TabsTrigger value="sessions">
            <Users className="mr-2 h-4 w-4" />
            Active Sessions
          </TabsTrigger>
          <TabsTrigger value="messages">
            <Bell className="mr-2 h-4 w-4" />
            Global Messages
          </TabsTrigger>
        </TabsList>

        {/* Schedule & Warning Tab */}
        <TabsContent value="status" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Schedule Maintenance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Schedule Maintenance
                </CardTitle>
                <CardDescription>
                  Plan maintenance in advance and notify users
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={scheduleForm.scheduled_at}
                    onChange={(e) => setScheduleForm({...scheduleForm, scheduled_at: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Estimated Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={scheduleForm.duration_minutes}
                    onChange={(e) => setScheduleForm({...scheduleForm, duration_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Message to Users</Label>
                  <Textarea
                    value={scheduleForm.message}
                    onChange={(e) => setScheduleForm({...scheduleForm, message: e.target.value})}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>ETA Message (optional)</Label>
                  <Input
                    placeholder="e.g., Back by 3:00 PM"
                    value={scheduleForm.eta_message}
                    onChange={(e) => setScheduleForm({...scheduleForm, eta_message: e.target.value})}
                  />
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={scheduleForm.send_email}
                      onCheckedChange={(v) => setScheduleForm({...scheduleForm, send_email: v})}
                    />
                    <Label className="flex items-center gap-1">
                      <Mail className="h-4 w-4" /> Send Email
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={scheduleForm.send_sms}
                      onCheckedChange={(v) => setScheduleForm({...scheduleForm, send_sms: v})}
                    />
                    <Label className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" /> Send SMS
                    </Label>
                  </div>
                </div>
                <Button onClick={handleScheduleMaintenance} className="w-full" data-testid="schedule-btn">
                  <Clock className="mr-2 h-4 w-4" />
                  Schedule Maintenance
                </Button>
              </CardContent>
            </Card>

            {/* Send Warning */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Send Warning Banner
                </CardTitle>
                <CardDescription>
                  Display a countdown warning to all logged-in users
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Warning Message</Label>
                  <Textarea
                    value={warningForm.message}
                    onChange={(e) => setWarningForm({...warningForm, message: e.target.value})}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Countdown (minutes)</Label>
                  <Input
                    type="number"
                    value={warningForm.countdown_minutes}
                    onChange={(e) => setWarningForm({...warningForm, countdown_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <Button onClick={handleSendWarning} variant="outline" className="w-full border-amber-500 text-amber-600 hover:bg-amber-50" data-testid="warning-btn">
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Send Warning Banner
                </Button>
                
                {status?.warning_active && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Warning Active:</strong> {status.warning_message}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Active Sessions Tab */}
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle>Active User Sessions</CardTitle>
              <CardDescription>
                Currently logged-in users across the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : sessions ? (
                <div className="space-y-6">
                  <div className="text-center py-8">
                    <p className="text-5xl font-bold text-blue-600">{sessions.total_active_sessions}</p>
                    <p className="text-gray-500 mt-2">Total Active Sessions</p>
                  </div>
                  
                  {sessions.by_role && Object.keys(sessions.by_role).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">By Role</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(sessions.by_role).map(([role, count]) => (
                          <Badge key={role} variant="outline" className="px-3 py-1">
                            {role}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-500">
                    Companies with active users: {sessions.by_company}
                  </p>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No session data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Messages Tab */}
        <TabsContent value="messages" className="space-y-6">
          {/* Send New Message */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Send Global Message
              </CardTitle>
              <CardDescription>
                Broadcast announcements to all users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    placeholder="Message title"
                    value={messageForm.title}
                    onChange={(e) => setMessageForm({...messageForm, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md"
                    value={messageForm.message_type}
                    onChange={(e) => setMessageForm({...messageForm, message_type: e.target.value})}
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="urgent">Urgent</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  placeholder="Your message to all users..."
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={messageForm.send_email}
                    onCheckedChange={(v) => setMessageForm({...messageForm, send_email: v})}
                  />
                  <Label className="flex items-center gap-1">
                    <Mail className="h-4 w-4" /> Also Send Email
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={messageForm.send_sms}
                    onCheckedChange={(v) => setMessageForm({...messageForm, send_sms: v})}
                  />
                  <Label className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4" /> Also Send SMS
                  </Label>
                </div>
              </div>
              <Button onClick={handleSendGlobalMessage} data-testid="send-message-btn">
                <Send className="mr-2 h-4 w-4" />
                Send Message
              </Button>
            </CardContent>
          </Card>

          {/* Active Messages */}
          <Card>
            <CardHeader>
              <CardTitle>Active Global Messages</CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No active global messages</p>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            msg.message_type === 'warning' ? 'default' :
                            msg.message_type === 'urgent' ? 'destructive' :
                            'secondary'
                          }>
                            {msg.message_type}
                          </Badge>
                          <span className="font-semibold">{msg.title}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{msg.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Created: {new Date(msg.created_at).toLocaleString()} by {msg.created_by}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteMessage(msg.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activate Confirmation Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Activate Maintenance Mode
            </DialogTitle>
            <DialogDescription>
              This will immediately:
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Log out all non-superadmin users</li>
                <li>Block all new logins</li>
                <li>Display maintenance page to everyone</li>
              </ul>
              <p className="mt-3 font-semibold">Are you sure you want to proceed?</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleActivate}>
              Activate Maintenance Mode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Power className="h-5 w-5" />
              Deactivate Maintenance Mode
            </DialogTitle>
            <DialogDescription>
              This will restore normal operation and allow users to login again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeactivate} className="bg-green-600 hover:bg-green-700">
              Deactivate & Resume Operations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenanceManagement;
